import { FC, HTMLProps, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { VerwijderenContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { parseValidEnumeration, parseValidRange } from '@/components/search/v1/utils/searchValidations';
import useSidebarStore from '@/components/sidebar/hooks/useSidebarStore';
import { mapCriteriaVoorSignalenToevoegen } from '@/components/sidebar/SignalenLijstHelper';
import {
  nummerComparator,
  SorteringRichting,
  SorteringType,
  stringComparator,
  tijdComparator,
} from '@/components/sidebar/sortField/useSorting';
import {
  GewijzigdeInzetSignaal,
  MaterieelType,
  NieuweClaimSignaal,
  OmleidingSignaal,
  OpheffenSignaal,
  Signaal,
  VerlengenSignaal,
  VersterkenSignaal,
  VerwijderdeClaimSignaal,
  VerzwakSignaal,
  WisselenSignaal,
} from '@/types/Signalen';
import { combineFilters } from '@/utils/functions';
import { createContextMenuData } from '@/utils/helpers';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { SignalenFilter } from './signalenFilter/signalenFilterModel';
import SignalenRow, { getSignaalType, SignaalPositie, signaalTypes } from './SignalenRow';

import styles from './Signalen.module.css';

const eenhedenMbtSignaal = (signaal: Signaal) => {
  const result: number[] = [];
  if ('eenheidNummer' in signaal) {
    result.push(Number(signaal.eenheidNummer));
  }
  if ('betrokkenWerklijn' in signaal && signaal.betrokkenWerklijn.eenheidNummer) {
    result.push(Number(signaal.betrokkenWerklijn.eenheidNummer));
  }
  if ('betrokkenWerklijnen' in signaal) {
    signaal.betrokkenWerklijnen.forEach((bw) => bw.eenheidNummer && result.push(Number(bw.eenheidNummer)));
  }
  if ('alleBetrokkenWerklijnen' in signaal) {
    signaal.alleBetrokkenWerklijnen.forEach((bw) => bw.eenheidNummer && result.push(Number(bw.eenheidNummer)));
  }
  return result;
};

const treinnummersMbtSignaal = (signaal: Signaal) => {
  const result: number[] = [];
  if ('bewegingSleutel' in signaal) {
    result.push(signaal.bewegingSleutel.treinnummer);
  }
  if ('inzetVoorMutatie' in signaal && signaal.inzetVoorMutatie.type === 'InzetOpBeweging') {
    result.push(signaal.inzetVoorMutatie.bewegingSleutel.treinnummer);
  }
  if ('inzetNaMutatie' in signaal && signaal.inzetNaMutatie.type === 'InzetOpBeweging') {
    result.push(signaal.inzetNaMutatie.bewegingSleutel.treinnummer);
  }
  if ('naInzet' in signaal && signaal.naInzet?.type === 'InzetOpBeweging') {
    result.push(signaal.naInzet.bewegingSleutel.treinnummer);
  }
  if ('eersteNieuweInzet' in signaal && signaal.eersteNieuweInzet?.type === 'InzetOpBeweging') {
    result.push(signaal.eersteNieuweInzet.bewegingSleutel.treinnummer);
  }
  return result;
};

const dienstregelpuntenMbtSignaal = (signaal: Signaal) => {
  const result: string[] = [];
  if ('dienstregelpuntenVanGemuteerdeBeweging' in signaal) {
    result.push(...signaal.dienstregelpuntenVanGemuteerdeBeweging);
  }
  if ('traject' in signaal) {
    result.push(signaal.traject.dienstregelpuntVan, signaal.traject.dienstregelpuntTot);
  }
  if ('versterktTraject' in signaal) {
    result.push(signaal.versterktTraject.dienstregelpuntVan, signaal.versterktTraject.dienstregelpuntTot);
  }
  if ('verzwaktTraject' in signaal) {
    result.push(signaal.verzwaktTraject.dienstregelpuntVan, signaal.verzwaktTraject.dienstregelpuntTot);
  }
  if ('omgeleidTraject' in signaal) {
    result.push(signaal.omgeleidTraject.dienstregelpuntVan, signaal.omgeleidTraject.dienstregelpuntTot);
  }
  if ('opgehevenTraject' in signaal) {
    result.push(signaal.opgehevenTraject.dienstregelpuntVan, signaal.opgehevenTraject.dienstregelpuntTot);
  }
  if ('verlengdTraject' in signaal) {
    result.push(signaal.verlengdTraject.dienstregelpuntVan, signaal.verlengdTraject.dienstregelpuntTot);
  }
  if ('inzetVoorMutatie' in signaal) {
    result.push(
      signaal.inzetVoorMutatie.traject.dienstregelpuntVan,
      signaal.inzetVoorMutatie.traject.dienstregelpuntTot,
    );
  }
  if ('inzetNaMutatie' in signaal) {
    result.push(signaal.inzetNaMutatie.traject.dienstregelpuntVan, signaal.inzetNaMutatie.traject.dienstregelpuntTot);
  }
  if ('naInzet' in signaal && signaal.naInzet) {
    result.push(signaal.naInzet.traject.dienstregelpuntVan, signaal.naInzet.traject.dienstregelpuntTot);
  }
  if ('eersteNieuweInzet' in signaal && signaal.eersteNieuweInzet) {
    result.push(
      signaal.eersteNieuweInzet.traject.dienstregelpuntVan,
      signaal.eersteNieuweInzet.traject.dienstregelpuntTot,
    );
  }
  return result;
};

const matType = (matType: MaterieelType | null) => (matType ? `${matType.soort}-${matType.aanduiding}` : 'onbekend');

const materieelTypesMbtSignaal = (signaal: Signaal) => {
  const result: string[] = [];
  if ('materieelType' in signaal) {
    result.push(matType(signaal.materieelType));
  }
  if ('betrokkenWerklijn' in signaal) {
    result.push(matType(signaal.betrokkenWerklijn.materieelType));
  }
  if ('betrokkenWerklijnen' in signaal) {
    console.log(JSON.stringify(signaal));
    result.push(...signaal.betrokkenWerklijnen.map((bw) => matType(bw.materieelType)));
  }
  if ('alleBetrokkenWerklijnen' in signaal) {
    result.push(...signaal.alleBetrokkenWerklijnen.map((bw) => matType(bw.materieelType)));
  }
  return result;
};

const filterSignaalOpEenheidEnTreinnummer = (eenheidOfTreinnummer?: string) => (signaal: Signaal) => {
  if (!eenheidOfTreinnummer) {
    return true;
  }

  const validEnumeration = parseValidEnumeration(eenheidOfTreinnummer);
  const validEenheidOfTreinRange = parseValidRange(eenheidOfTreinnummer);
  const gecombineerdeLijstVanNummmersMbtSignaal = [...eenhedenMbtSignaal(signaal), ...treinnummersMbtSignaal(signaal)];

  if (validEnumeration) {
    return gecombineerdeLijstVanNummmersMbtSignaal.some((t) => validEnumeration.includes(t));
  }
  if (validEenheidOfTreinRange) {
    const [van, tot] = validEenheidOfTreinRange;
    return gecombineerdeLijstVanNummmersMbtSignaal.some((t) => t >= van && t <= tot);
  }
  return gecombineerdeLijstVanNummmersMbtSignaal.includes(Number(eenheidOfTreinnummer));
};

const filterSignaalOpDienstregelpunten = (dienstregelpunten?: string[]) => (signaal: Signaal) => {
  if (!dienstregelpunten || dienstregelpunten.length === 0 || signaal.type === 'InzetbaarheidSignaal') {
    return true;
  }
  return dienstregelpuntenMbtSignaal(signaal).some((d) => dienstregelpunten.includes(d));
};

const filterSignaalOpMaterieelType = (materieelTypen?: string[]) => (signaal: Signaal) => {
  if (!materieelTypen || materieelTypen.length === 0) {
    return true;
  }
  return materieelTypesMbtSignaal(signaal).some((sm) =>
    materieelTypen.some((mt) => mt.toUpperCase() === sm.toUpperCase()),
  );
};

const filterSignaalOpAlleenWts = (alleenWts?: boolean) => (signaal: Signaal) => {
  if (!alleenWts) {
    return true;
  }
  return signaal.actor.toUpperCase() === 'WTS';
};

const filterSignaalOpAlleenManco = (alleenManco?: boolean) => (signaal: Signaal) => {
  if (!alleenManco) {
    return true;
  }

  const signaalType = signaal.type ? getSignaalType(signaal) : '';
  switch (signaalType) {
    case signaalTypes.WISSEL:
      return (signaal as WisselenSignaal)?.betrokkenWerklijn.eenheidNummer === null;
    case signaalTypes.GEWIJZIGD:
      return (signaal as GewijzigdeInzetSignaal)?.betrokkenWerklijn.eenheidNummer === null;
    case signaalTypes.CLAIM: {
      const isMancoNieuweClaim = (signaal as NieuweClaimSignaal)?.betrokkenWerklijn?.eenheidNummer === null;
      const isMancoVerwijderdeClaim = (signaal as VerwijderdeClaimSignaal)?.betrokkenWerklijn?.eenheidNummer === null;
      return isMancoNieuweClaim || isMancoVerwijderdeClaim;
    }
    case signaalTypes.OMGELEID:
      return (signaal as OmleidingSignaal)?.betrokkenWerklijnen.some((wl) => wl.eenheidNummer === null);
    case signaalTypes.OPGEHEVEN:
      return (signaal as OpheffenSignaal)?.betrokkenWerklijnen.some((wl) => wl.eenheidNummer === null);
    case signaalTypes.VERLENGD:
      return (signaal as VerlengenSignaal)?.betrokkenWerklijnen.some((wl) => wl.eenheidNummer === null);
    case signaalTypes.VERSTERKT:
      return (signaal as VersterkenSignaal)?.alleBetrokkenWerklijnen.some((wl) => wl.eenheidNummer === null);
    case signaalTypes.VERZWAKT:
      return (signaal as VerzwakSignaal)?.alleBetrokkenWerklijnen.some((wl) => wl.eenheidNummer === null);
    case signaalTypes.INZETBAARHEID:
      // Inzetbaar signaal kan niet voorkomen op een manco werklijn
      return false;
    default:
      return false;
  }
};

const filterSignaalOpSignaalType = (signaalTypen?: string[]) => (signaal: Signaal) => {
  if (!signaalTypen || signaalTypen.length === 0) {
    return true;
  }
  const signaalType = signaal.type ? getSignaalType(signaal) : '';

  return signaalTypen.some((mt) => mt.toLowerCase() === signaalType.toLowerCase());
};

const filterSignaalOpInregelingen = (alleenMbtInregelingen?: boolean) => (signaal: Signaal) => {
  if (!alleenMbtInregelingen) {
    return true;
  }
  if (signaal.type === 'InregelingSignaal') {
    return true;
  }
  if ('betrokkenWerklijn' in signaal && signaal.betrokkenWerklijn.inregelDatum) {
    return true;
  }
  if ('betrokkenWerklijnen' in signaal && signaal.betrokkenWerklijnen.some((bw) => bw.inregelDatum)) {
    return true;
  }
  if ('inregelDatum' in signaal && signaal.inregelDatum) {
    return true;
  }
  return 'alleBetrokkenWerklijnen' in signaal && signaal.alleBetrokkenWerklijnen.some((bw) => bw.inregelDatum);
};

const teSorterenEenheidMbtSignaal = (signaal: Signaal) => {
  const result: number[] = [];
  if ('eenheidNummer' in signaal) {
    result.push(Number(signaal.eenheidNummer));
  }
  if ('betrokkenWerklijn' in signaal && signaal.betrokkenWerklijn.eenheidNummer) {
    result.push(Number(signaal.betrokkenWerklijn.eenheidNummer));
  }
  if ('betrokkenWerklijnen' in signaal) {
    signaal.betrokkenWerklijnen.forEach((bw) => bw.eenheidNummer && result.push(Number(bw.eenheidNummer)));
  }
  if ('versterkteWerklijn' in signaal && signaal.versterkteWerklijn.eenheidNummer) {
    result.push(Number(signaal.versterkteWerklijn.eenheidNummer));
  }
  if ('verzwakteWerklijn' in signaal && signaal.verzwakteWerklijn.eenheidNummer) {
    result.push(Number(signaal.verzwakteWerklijn.eenheidNummer));
  }

  return result.length === 0 ? null : Math.min(...result);
};

const teSorterenTreinnummerMbtSignaal = (signaal: Signaal) => {
  const result = treinnummersMbtSignaal(signaal);
  return result.length === 0 ? null : Math.min(...result);
};

const signaalComparator = (
  signaalA: Signaal,
  signaalB: Signaal,
  sortType: SorteringType | undefined,
  sortDirection: SorteringRichting | undefined,
) => {
  if (sortType === SorteringType.DATE_TIME) {
    return tijdComparator(signaalA.tijdstip, signaalB.tijdstip, sortDirection);
  }
  const fallbackComparator = tijdComparator(signaalA.tijdstip, signaalB.tijdstip, SorteringRichting.DOWN);
  if (sortType === SorteringType.EENHEID_NUMMER) {
    const eenheidA = teSorterenEenheidMbtSignaal(signaalA);
    const eenheidB = teSorterenEenheidMbtSignaal(signaalB);
    return nummerComparator(eenheidA, eenheidB, sortDirection, () => fallbackComparator);
  }
  if (sortType === SorteringType.TREIN_NUMMER) {
    const treinnummerA = teSorterenTreinnummerMbtSignaal(signaalA);
    const treinnummerB = teSorterenTreinnummerMbtSignaal(signaalB);
    return nummerComparator(treinnummerA, treinnummerB, sortDirection, () => fallbackComparator);
  }
  if (sortType === SorteringType.SIGNAAL_TYPE) {
    return stringComparator(signaalA.type, signaalB.type, sortDirection, () => fallbackComparator);
  }
  // Default sorteer op tijd down
  return fallbackComparator;
};

const filterEnSorteerSignalen = (signalen: Signaal[] | undefined, filter: SignalenFilter) => {
  if (!signalen) {
    return [];
  }
  const eenheidEnTreinnummerFilter = filterSignaalOpEenheidEnTreinnummer(filter.eenheidOfTreinnummer);
  const dienstregelpuntenFilter = filterSignaalOpDienstregelpunten(filter.dienstregelpunten);
  const materieelTypenFilter = filterSignaalOpMaterieelType(filter.materieelTypen);
  const signaalTypenFilter = filterSignaalOpSignaalType(filter.signaalTypen);
  const inregelingenFilter = filterSignaalOpInregelingen(filter.ingeregeldMat);
  const wtsFilter = filterSignaalOpAlleenWts(filter.alleenWts);
  const mancoFilter = filterSignaalOpAlleenManco(filter.alleenManco);

  return signalen
    .filter(
      combineFilters(
        dienstregelpuntenFilter,
        materieelTypenFilter,
        signaalTypenFilter,
        inregelingenFilter,
        wtsFilter,
        mancoFilter,
        eenheidEnTreinnummerFilter,
      ),
    )
    .sort((signaalA, signaalB) =>
      signaalComparator(signaalA, signaalB, filter.sorteringType, filter.sorteringRichting),
    );
};
const SignalenTable: FC<
  {
    signalen: Signaal[];
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ signalen, panelMenuInformation, ...rest }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const activePanel = useSidebarStore((state) => state.activePanel);
  const setActivePanel = useSidebarStore((state) => state.setActivePanel);
  const setData = useSidebarStore((state) => state.setData);

  const rowVirtualizer = useVirtualizer({
    count: signalen?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 20,
  });

  const signaalPositie: (index: number) => SignaalPositie = (index) => {
    if (signalen[index].indexInBericht === 0) {
      return signalen[index + 1]?.indexInBericht === 0 ||
        signalen[index + 1]?.correlatieId !== signalen[index].correlatieId
        ? 'enkelvoudig'
        : 'eerste';
    }
    return signalen[index].correlatieId !== signalen[index + 1]?.correlatieId ? 'laatste' : 'middelste';
  };

  const [selectedSignalen, setSelectedSignalen] = useState<Signaal[]>(() => {
    const saved = sessionStorage.getItem('selectedSignalen');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    sessionStorage.setItem('selectedSignalen', JSON.stringify(selectedSignalen));
    setData('signalen', selectedSignalen);
  }, [selectedSignalen]);

  useEffect(() => {
    if (panelMenuInformation.activePanel !== 'Signalen' || activePanel !== 'Signalen') {
      setSelectedSignalen([]);
    }
  }, [panelMenuInformation.activePanel, activePanel]);

  const handleOnSignaalClick = (event: React.MouseEvent<HTMLDivElement>, signaal: Signaal) => {
    panelMenuInformation.setActivePanel('Signalen');
    setActivePanel('Signalen');
    if (event.button === 2) {
      event.preventDefault();
      let signalenVoorMwd: Signaal[];
      if (!selectedSignalen.includes(signaal)) {
        setSelectedSignalen([signaal]);
        // zet in signalenVoorMwd omdat selectedSignalen nog niet beschikbaar issetActivePanel(undefined);
        // wanneer de contextMenuData wordt gemaakt
        signalenVoorMwd = [signaal];
      } else {
        signalenVoorMwd = selectedSignalen;
      }
      const verwijderenOptions: VerwijderenContextMenuDetails = { showMenu: true, recordCount: signalenVoorMwd.length };

      const options: { panelType?: 'signalen' | 'defecten' | 'conflicten' }[] = [
        {
          panelType: 'signalen',
        },
      ];

      const contextMenuData = createContextMenuData(
        event,
        mapCriteriaVoorSignalenToevoegen(signalenVoorMwd),
        verwijderenOptions,
        options,
      );

      panelMenuInformation.contextMenuInformationCallback(contextMenuData);
    }
    // selecteren met ctrl key
    else if (event.ctrlKey) {
      event.preventDefault();
      setSelectedSignalen((prev) => {
        if (prev.includes(signaal)) {
          return prev.filter((id) => id !== signaal);
        }
        return [...prev, signaal];
      });
      // selecteren met shift key
    } else if (event.shiftKey) {
      event.preventDefault();
      if (selectedSignalen.length === 0) {
        setSelectedSignalen([signaal]);
        return;
      }
      const currentIndex = signalen.findIndex(
        (s) => s.berichtId === signaal.berichtId && s.indexInBericht === signaal.indexInBericht,
      );
      const lastSelectedIndex = signalen.findIndex(
        (s) => s.berichtId === selectedSignalen[selectedSignalen.length - 1].berichtId,
      );
      const start = Math.min(currentIndex, lastSelectedIndex);
      const end = Math.max(currentIndex, lastSelectedIndex);
      setSelectedSignalen(signalen.slice(start, end + 1));
    } else if (selectedSignalen.includes(signaal)) {
      setSelectedSignalen([]);
    } else {
      setSelectedSignalen([signaal]);
    }
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    panelMenuInformation.setActivePanel('Signalen');
    setActivePanel('Signalen');
    event.preventDefault();
    if (event.key === 'Delete') {
      const existingDeletedSignalen = sessionStorage.getItem('deletedSignalen');
      const parsedDeletedSignalen = existingDeletedSignalen ? JSON.parse(existingDeletedSignalen) : [];
      const updatedDeletedSignalen = [
        ...new Set([...parsedDeletedSignalen, ...selectedSignalen.map((signaal) => signaal.berichtId)]),
      ];
      sessionStorage.setItem('deletedSignalen', JSON.stringify(updatedDeletedSignalen));
      window.dispatchEvent(new Event('storage'));
      setSelectedSignalen([]);
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      setSelectedSignalen(signalen);
    }

    if (event.altKey && event.code === 'KeyT') {
      panelMenuInformation.handleSelectieUitPanel(mapCriteriaVoorSignalenToevoegen(selectedSignalen));
      panelMenuInformation.setActivePanel(undefined);
    }
  };

  return (
    signalen && (
      <div ref={parentRef} className={styles.signalenScrollableStyle} {...rest}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <SignalenRow
              signaal={signalen[virtualItem.index]}
              handleOnSignaalClick={(event) => handleOnSignaalClick(event, signalen[virtualItem.index])}
              handleOnKeyDown={handleOnKeyDown}
              positie={signaalPositie(virtualItem.index)}
              key={`${signalen[virtualItem.index]?.berichtId}_${signalen[virtualItem.index]?.indexInBericht}`}
              data-selected={selectedSignalen.includes(signalen[virtualItem.index])}
              data-dtname='signalenlijst-element'
              style={{
                position: 'absolute',
                background:
                  ((panelMenuInformation && panelMenuInformation.activePanel === 'Signalen') ||
                    activePanel === 'Signalen') &&
                  selectedSignalen.some(
                    (value) =>
                      value.berichtId === signalen[virtualItem.index].berichtId &&
                      value.indexInBericht === signalen[virtualItem.index].indexInBericht,
                  )
                    ? 'var(--tractie-color-background-selected-default)'
                    : '',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      </div>
    )
  );
};

const MemoizedSignalenTable = memo(SignalenTable);

const SignalenLijst: FC<
  {
    signalen: Signaal[];
    filter: SignalenFilter;
    panelMenuInformation;
  } & HTMLProps<HTMLDivElement>
> = ({ signalen, filter, panelMenuInformation, ...rest }) => {
  const deletedSignalen = sessionStorage.getItem('deletedSignalen');
  const [storageAlert, setStorageAlert] = useState<boolean>(false);

  const filterSignalenOpLocaalVerwijderdeItems = useCallback(
    (signalen: Signaal[]) => {
      if (!deletedSignalen) {
        storageAlert && setStorageAlert(false);
        return signalen;
      }
      const parsedSignalen = JSON.parse(deletedSignalen);
      storageAlert && setStorageAlert(false);
      return signalen.filter((signaal) => !parsedSignalen.some((parsedSignaal) => parsedSignaal === signaal.berichtId));
    },
    [deletedSignalen, storageAlert],
  );

  // Even listener om te luisteren naar storage events
  useEffect(() => {
    const handleStorageChange = () => setStorageAlert(true);
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const gefilterdeSignalen = useMemo(
    () => filterEnSorteerSignalen(filterSignalenOpLocaalVerwijderdeItems(signalen), filter),
    [filterSignalenOpLocaalVerwijderdeItems, signalen, filter],
  );

  return <MemoizedSignalenTable signalen={gefilterdeSignalen} {...rest} panelMenuInformation={panelMenuInformation} />;
};

export default SignalenLijst;
